import { useRef, useState, useEffect, useCallback } from "react";

const useCamera = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [facingMode, setFacingMode] = useState("environment");
  const [videoDevices, setVideoDevices] = useState([]);
  const [currentDeviceId, setCurrentDeviceId] = useState(null);
  const [error, setError] = useState(null);

  // 🚫 Stop current camera stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // 📸 Capture current frame as base64 image
  const captureImage = () => {
    if (!videoRef.current || !canvasRef.current) return null;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

    return canvas.toDataURL("image/png");
  };

  // 📱 Get list of available video input devices
  const getDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter((d) => d.kind === "videoinput");
      setVideoDevices(videoInputs);
    } catch (err) {
      console.error("Could not enumerate devices:", err);
    }
  }, []);

  // ✅ Try to start the camera with fallback
  const startCamera = useCallback(
    async (deviceId = currentDeviceId, mode = facingMode) => {
      stopCamera();
      setError(null);

      let stream = null;

      const tryStart = async (constraints) => {
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraints);
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            await videoRef.current.play();
          }
          return true;
        } catch (err) {
          console.error("Camera start failed:", err);
          return false;
        }
      };

      // 🎯 Attempt with preferred constraints
      const success = await tryStart(
        deviceId
          ? { video: { deviceId: { exact: deviceId } }, audio: false }
          : { video: { facingMode: mode }, audio: false }
      );

      // 🧯 If failed, try basic fallback
      if (!success) {
        console.warn("Falling back to default camera");
        const fallbackSuccess = await tryStart({ video: true });
        if (!fallbackSuccess) {
          setError("Unable to access camera on this device.");
        }
      }
    },
    [currentDeviceId, facingMode, stopCamera]
  );

  // 🔄 Flip between front/back camera
  const flipCamera = () => {
    if (videoDevices.length < 2) return;

    const isFront = facingMode === "user";
    const target = isFront ? "back" : "front";

    const targetDevice = videoDevices.find((d) =>
      d.label.toLowerCase().includes(target)
    );

    if (targetDevice) {
      setCurrentDeviceId(targetDevice.deviceId);
      setFacingMode(isFront ? "environment" : "user");
    } else {
      const currentIndex = videoDevices.findIndex(
        (d) => d.deviceId === currentDeviceId
      );
      const nextIndex = (currentIndex + 1) % videoDevices.length;
      setCurrentDeviceId(videoDevices[nextIndex].deviceId);
    }
  };

  // 🎯 On mount: request permission + fetch devices
  useEffect(() => {
    const init = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ video: true });
        await getDevices();
      } catch (err) {
        console.error("Camera permission denied:", err);
        setError("Camera access denied or not supported.");
      }
    };
    init();
  }, [getDevices]);

  // 🎯 Once devices available, pick default
  useEffect(() => {
    if (videoDevices.length > 0) {
      const preferred =
        facingMode === "user"
          ? videoDevices.find((d) => d.label.toLowerCase().includes("front"))
          : videoDevices.find((d) => d.label.toLowerCase().includes("back"));

      const fallback = videoDevices[0];
      const chosen = preferred || fallback;

      if (chosen) {
        setCurrentDeviceId(chosen.deviceId);
      }
    }
  }, [videoDevices, facingMode]);

  // 🚀 Start camera on deviceId change
  useEffect(() => {
    if (currentDeviceId) {
      startCamera(currentDeviceId);
    }
  }, [currentDeviceId, startCamera]);

  return {
    videoRef,
    canvasRef,
    flipCamera,
    captureImage,
    stopCamera,
    startCamera,
    facingMode,
    error,
  };
};

export default useCamera;
